import {
  reorder_form_section,
  reorder_main_sections,
} from '@/services/main_section_service';
import { reorder_section_question } from '@/services/question_service';
import { extract_error_message } from '@/utils/api_response_helpers';

export function resolveMainSectionReorderContext(ctx, source_index, target_index, isValidReorderRange) {
  const form_id = Number(ctx.topBar.formId);
  const ordered_sections = [...ctx.section_groups];
  const has_valid_range = isValidReorderRange(source_index, target_index, ordered_sections);
  const reorder_context = {
    form_id,
    has_valid_range,
    ordered_sections,
  };
  return reorder_context;
}

export function performReorderMainSections(ctx, payload) {
  const reorder_result = reorder_main_sections(payload)
    .then((parsed_response) => {
      if (parsed_response?.status !== 'success') {
        ctx.show_error(
          parsed_response?.message || 'Failed to reorder main sections.'
        );
        return null;
      }

      const load_result = ctx.load_main_sections().then(() => {
        ctx.show_success(
          parsed_response.message || 'Main sections reordered successfully.'
        );
      });
      return load_result;
    })
    .catch((error) => {
      ctx.show_error(
        extract_error_message(error, 'Failed to reorder main sections.')
      );
    });
  return reorder_result;
}

export function resolveSubSectionReorderContext(
  main_section,
  source_index,
  target_index,
  isValidReorderRange,
  moveItemByIndex,
  resolveMainSectionId,
  resolveSubSectionId
) {
  const form_main_section_id = resolveMainSectionId(main_section);
  const ordered_sections = Array.isArray(main_section?.sections) ? [...main_section.sections] : [];
  const has_valid_range = isValidReorderRange(source_index, target_index, ordered_sections);
  const moved_result = has_valid_range ? moveItemByIndex(ordered_sections, source_index, target_index) : null;
  const form_section_id = resolveSubSectionId(moved_result?.moved_item);
  const sub_section_resolve = {
    form_main_section_id,
    form_section_id,
    moved_result,
  };
  return sub_section_resolve;
}

export function performReorderSubSections(ctx,reorder_context,target_index) {
  return reorder_form_section({
    form_section_id: reorder_context.form_section_id,
    form_main_section_id: reorder_context.form_main_section_id,
    order_number: target_index + 1,
  })
    .then((parsed_response) => {
      const success = parsed_response?.status === 'success';

      if (!success) {
        ctx.show_error(
          parsed_response?.message || 'Failed to reorder sections.'
        );
        return null;
      }

      return ctx.load_main_sections().then(() => {
        ctx.expand_main_section_by_id(
          reorder_context.form_main_section_id
        );
        ctx.show_success(
          parsed_response.message || 'Sections reordered successfully.'
        );
      });
    })
    .catch((error) => {
      ctx.show_error(
        extract_error_message(error, 'Failed to reorder sections.')
      );
    });
}

function getAvailableSectionQuestionIds(question = {}) {
  return [
    question.section_question_id,
    question.question_id,
    question.backend_id,
    question.id,
  ];
}

function resolveSectionQuestionId(question = {}) {
  const available_question_ids = getAvailableSectionQuestionIds(question)
    .find((value) => value != null);
  const section_question_id = available_question_ids != null ? Number(available_question_ids) : null;
  return section_question_id;
}

export function resolveQuestionReorderContext(
  section,
  source_index,
  target_index,
  isValidReorderRange,
  moveItemByIndex
) {
  const ordered_questions = Array.isArray(section?.questions) ? [...section.questions] : [];
  const has_valid_range = isValidReorderRange(source_index, target_index, ordered_questions);
  const moved_result = has_valid_range ? moveItemByIndex(ordered_questions, source_index, target_index) : null;
  const section_question_id = resolveSectionQuestionId(moved_result?.moved_item);
  const question_reorder = {
    moved_result,
    section_question_id,
  };
  return question_reorder;
}

function getSectionId(section = {}) {
  return section.section_id ?? section.backend_id ?? section.id ?? null;
}

export async function performReorderSectionQuestions(ctx, section, reorder_context, target_index) {
  try {
    const parsed_response = await reorder_section_question({
      section_question_id: reorder_context.section_question_id,
      order_number: target_index + 1,
    });
    const success = parsed_response?.status === 'success';

    if (!success) {
      ctx.show_error(
        parsed_response?.message || 'Failed to reorder questions.'
      );
      return;
    }

    await ctx.load_main_sections();
    ctx.expand_section_by_id(getSectionId(section));
    ctx.show_success(
      parsed_response.message || 'Questions reordered successfully.'
    );
  } catch (error) {
    ctx.show_error(
      extract_error_message(error, 'Failed to reorder questions.')
    );
  }
}
